import { useQuery } from "@tanstack/react-query";
import { getTokens } from "../utils/auth";
import { getAdminStatisticsApiAdminImagesStatisticsGet } from "../api/generated";

export interface AdminStatistics {
  total_galleries: number;
  total_images: number;
  total_images_db: number;
  cloudinary_images_count: number;
  total_storage_formatted: string;
  total_storage_bytes: number;
  cloudinary_storage_formatted: string;
  cloudinary_storage_bytes: number;
  total_storage_db_formatted: string;
  total_storage_db_bytes: number;
  public_images: number;
  private_images: number;
  featured_images: number;
  recent_uploads_7_days: number;
  total_views: number;
  recent_views_30d: number;
  recent_views_7d: number;
  unique_images_viewed: number;
}

/**
 * Hook for fetching admin dashboard statistics.
 */
export function useAdminStats() {
  const { accessToken } = getTokens();
  const isDevelopment = process.env.NODE_ENV === "development";

  return useQuery<AdminStatistics>({
    queryKey: ["admin", "statistics"],
    queryFn: async () => {
      // In development, allow requests even without token (DISABLE_AUTH mode)
      if (!accessToken && !isDevelopment) {
        console.error("[useAdminStats] No access token found");
        throw new Error("No access token found. Please log in.");
      }

      try {
        console.log("[useAdminStats] Fetching admin statistics...");
        const response = await getAdminStatisticsApiAdminImagesStatisticsGet();
        console.log("[useAdminStats] Response received:", response);

        // Check if response has an error
        if ("error" in response && response.error) {
          console.error("[useAdminStats] API error:", response.error);
          throw new Error(
            `Failed to fetch admin statistics: ${JSON.stringify(
              response.error
            )}`
          );
        }

        // Check if data exists
        if (!response.data) {
          console.error("[useAdminStats] No data in response:", response);
          throw new Error("Failed to fetch admin statistics: No data returned");
        }

        console.log("[useAdminStats] Statistics data:", response.data);
        return response.data as AdminStatistics;
      } catch (error: any) {
        console.error("[useAdminStats] Error fetching statistics:", error);
        // Log more details about the error
        if (error?.response) {
          console.error("[useAdminStats] Error response:", error.response);
        }
        if (error?.message) {
          console.error("[useAdminStats] Error message:", error.message);
        }
        throw error;
      }
    },
    // Enable query if we have a token OR if we're in development mode (DISABLE_AUTH)
    enabled: !!accessToken || isDevelopment,
    staleTime: 1000 * 60 * 5, // 5 minutes
    refetchOnWindowFocus: true,
    retry: 1, // Only retry once
  });
}
